import { CourseHierarchy, LearningLevel } from '@/types/learningPathTypes';

// Course hierarchy mapping - defines prerequisites and unlocks for each course
export const COURSE_HIERARCHY: Record<string, CourseHierarchy> = {
  // Foundation Courses
  'html-css-fundamentals': {
    level: 'foundation',
    tracks: ['frontend', 'fullstack'],
    prerequisites: [],
    unlocks: ['javascript-fundamentals'],
    estimatedDifficulty: 2
  },
  'git-version-control': {
    level: 'foundation',
    tracks: ['frontend', 'backend', 'fullstack', 'devops'],
    prerequisites: [],
    unlocks: ['javascript-fundamentals', 'python-fundamentals'],
    estimatedDifficulty: 3
  },
  'programming-basics': {
    level: 'foundation',
    tracks: ['frontend', 'backend', 'fullstack', 'datascience'],
    prerequisites: [],
    unlocks: ['javascript-fundamentals', 'python-fundamentals'],
    estimatedDifficulty: 3
  },
  'http-rest-concepts': {
    level: 'foundation',
    tracks: ['frontend', 'backend', 'fullstack'],
    prerequisites: [],
    unlocks: ['api-development'],
    estimatedDifficulty: 4
  },

  // React Courses (React Development Category)
  'react-fundamentals': {
    level: 'beginner',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['javascript-fundamentals'],
    unlocks: ['react-hooks', 'react-intermediate'],
    estimatedDifficulty: 4
  },
  'react-hooks': {
    level: 'beginner',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['react-fundamentals'],
    unlocks: ['react-state-management', 'react-intermediate'],
    estimatedDifficulty: 5
  },
  'react-components': {
    level: 'beginner',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['react-fundamentals'],
    unlocks: ['react-advanced-patterns', 'react-intermediate'],
    estimatedDifficulty: 5
  },
  'react-state-management': {
    level: 'intermediate',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['react-hooks'],
    unlocks: ['react-advanced', 'react-patterns'],
    estimatedDifficulty: 6
  },
  'react-intermediate': {
    level: 'intermediate',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['react-hooks', 'react-components'],
    unlocks: ['react-advanced', 'react-patterns'],
    estimatedDifficulty: 6
  },
  'react-advanced': {
    level: 'advanced',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['react-state-management', 'react-intermediate'],
    unlocks: ['react-expert', 'react-typescript'],
    estimatedDifficulty: 7
  },

  // JavaScript/Frontend Courses
  'javascript-fundamentals': {
    level: 'beginner',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['html-css-fundamentals', 'programming-basics'],
    unlocks: ['javascript-intermediate', 'react-fundamentals'],
    estimatedDifficulty: 4
  },
  'javascript-intermediate': {
    level: 'beginner',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['javascript-fundamentals'],
    unlocks: ['react-fundamentals', 'javascript-advanced'],
    estimatedDifficulty: 5
  },
  'javascript-advanced': {
    level: 'intermediate',
    tracks: ['frontend', 'fullstack'],
    prerequisites: ['javascript-intermediate'],
    unlocks: ['react-advanced', 'node-js'],
    estimatedDifficulty: 6
  },

  // Python Courses
  'python-fundamentals': {
    level: 'beginner',
    tracks: ['backend', 'fullstack', 'datascience', 'ai'],
    prerequisites: ['programming-basics'],
    unlocks: ['python-intermediate', 'data-structures-python'],
    estimatedDifficulty: 4
  },
  'python-intermediate': {
    level: 'intermediate',
    tracks: ['backend', 'fullstack', 'datascience', 'ai'],
    prerequisites: ['python-fundamentals'],
    unlocks: ['python-advanced', 'django-fundamentals', 'data-analysis'],
    estimatedDifficulty: 5
  },
  'python-advanced': {
    level: 'advanced',
    tracks: ['backend', 'fullstack', 'datascience', 'ai'],
    prerequisites: ['python-intermediate'],
    unlocks: ['machine-learning', 'ai-fundamentals'],
    estimatedDifficulty: 7
  },

  // SQL Courses
  'sql-fundamentals': {
    level: 'beginner',
    tracks: ['backend', 'fullstack', 'datascience'],
    prerequisites: [],
    unlocks: ['sql-intermediate', 'database-design'],
    estimatedDifficulty: 3
  },
  'sql-intermediate': {
    level: 'intermediate',
    tracks: ['backend', 'fullstack', 'datascience'],
    prerequisites: ['sql-fundamentals'],
    unlocks: ['sql-advanced', 'data-modeling'],
    estimatedDifficulty: 5
  },
  'sql-advanced': {
    level: 'advanced',
    tracks: ['backend', 'fullstack', 'datascience'],
    prerequisites: ['sql-intermediate'],
    unlocks: ['database-optimization', 'data-warehousing'],
    estimatedDifficulty: 7
  },

  // AI/ML Courses
  'ai-fundamentals': {
    level: 'beginner',
    tracks: ['ai', 'datascience'],
    prerequisites: ['python-fundamentals'],
    unlocks: ['machine-learning', 'deep-learning'],
    estimatedDifficulty: 6
  },
  'machine-learning': {
    level: 'intermediate',
    tracks: ['ai', 'datascience'],
    prerequisites: ['ai-fundamentals', 'python-intermediate'],
    unlocks: ['deep-learning', 'nlp'],
    estimatedDifficulty: 7
  },
  'deep-learning': {
    level: 'advanced',
    tracks: ['ai'],
    prerequisites: ['machine-learning'],
    unlocks: ['computer-vision', 'nlp-advanced'],
    estimatedDifficulty: 8
  },

  // Cloud & DevOps Courses
  'cloud-fundamentals': {
    level: 'beginner',
    tracks: ['cloud', 'devops'],
    prerequisites: [],
    unlocks: ['aws-fundamentals', 'azure-fundamentals'],
    estimatedDifficulty: 4
  },
  'devops-fundamentals': {
    level: 'beginner',
    tracks: ['devops'],
    prerequisites: ['git-version-control'],
    unlocks: ['ci-cd', 'containerization'],
    estimatedDifficulty: 5
  },
  'containerization': {
    level: 'intermediate',
    tracks: ['devops', 'backend'],
    prerequisites: ['devops-fundamentals'],
    unlocks: ['kubernetes', 'microservices'],
    estimatedDifficulty: 6
  },

  // Mobile Development
  'mobile-fundamentals': {
    level: 'beginner',
    tracks: ['mobile'],
    prerequisites: ['programming-basics'],
    unlocks: ['react-native', 'ios-development'],
    estimatedDifficulty: 5
  },
  'react-native': {
    level: 'intermediate',
    tracks: ['mobile', 'frontend'],
    prerequisites: ['react-fundamentals', 'mobile-fundamentals'],
    unlocks: ['mobile-advanced'],
    estimatedDifficulty: 6
  }
};

// Utility functions for course hierarchy
export const getCoursePrerequisites = (courseId: string): string[] => {
  return COURSE_HIERARCHY[courseId]?.prerequisites || [];
};

export const getCourseUnlocks = (courseId: string): string[] => {
  return COURSE_HIERARCHY[courseId]?.unlocks || [];
};

export const getCourseDifficulty = (courseId: string): number => {
  return COURSE_HIERARCHY[courseId]?.estimatedDifficulty || 5;
};

export const getCourseLevel = (courseId: string): LearningLevel | undefined => {
  return COURSE_HIERARCHY[courseId]?.level;
};

export const getCoursesByLevel = (level: LearningLevel): string[] => {
  return Object.keys(COURSE_HIERARCHY).filter(
    courseId => COURSE_HIERARCHY[courseId].level === level
  );
};

export const getCoursesByTrack = (track: string): string[] => {
  return Object.keys(COURSE_HIERARCHY).filter(
    courseId => COURSE_HIERARCHY[courseId].tracks.includes(track as any)
  );
};

export const canAccessCourse = (courseId: string, completedCourses: string[]): boolean => {
  const prerequisites = getCoursePrerequisites(courseId);
  return prerequisites.every(prereq => completedCourses.includes(prereq));
};

export const getRecommendedNextCourses = (completedCourses: string[]): string[] => {
  const availableCourses = Object.keys(COURSE_HIERARCHY).filter(courseId => 
    !completedCourses.includes(courseId) && canAccessCourse(courseId, completedCourses)
  );
  
  return availableCourses.sort((a, b) => 
    getCourseDifficulty(a) - getCourseDifficulty(b)
  );
};

export const getLearningPath = (targetCourse: string, completedCourses: string[] = []): string[] => {
  const path: string[] = [];
  const visited = new Set<string>();
  
  const buildPath = (courseId: string) => {
    if (visited.has(courseId)) return;
    visited.add(courseId);
    
    const prerequisites = getCoursePrerequisites(courseId);
    prerequisites.forEach(prereq => {
      if (!completedCourses.includes(prereq)) {
        buildPath(prereq);
      }
    });
    
    if (!completedCourses.includes(courseId) && !path.includes(courseId)) {
      path.push(courseId);
    }
  };
  
  buildPath(targetCourse);
  return path;
};

export const getTrackProgression = (track: string): Record<LearningLevel, string[]> => {
  const trackCourses = getCoursesByTrack(track);
  const progression: Record<LearningLevel, string[]> = {
    foundation: [],
    beginner: [],
    intermediate: [],
    advanced: [],
    expert: []
  };
  
  trackCourses.forEach(courseId => {
    const level = getCourseLevel(courseId);
    if (level) {
      progression[level].push(courseId);
    }
  });
  
  return progression;
};